import { HumanResourcesItem } from '@/app/models/detailsRH';
import { SubGroup } from '@/app/models/financalGroup';
import { ProjectResponse } from '@/app/models/projects';
import { useSearchHumanResourcesQuery } from '@/app/services/detailsHRApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, FormControl, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import HumanResourcesTable from './HumanResourcesTable';
import NewEditHumanResourcesDetails from './NewEditHumanResourcesDetails';
import NewEditHumanResourcesDetailsPJ from './NewEditHumanResourcesDetailsPJ';
import NewEditRatioHumanResources from './NewEditRatioHumanResources';
import NewEditValuesHumanResources from './NewEditValuesHumanResources';
import NewHumanResourcesTransfer from './NewHumanResourcesTransfer';
import NewUnion from './NewUnion';

interface HumanResorceProps {
  groupCode: number;
}

function HumanResources(props: HumanResorceProps) {
  const theme = useTheme();
  const { t } = useTranslation(['humanResource', 'common']);
  const period = usePeriod();
  const { code } = useParams();
  const { groupCode } = props;
  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();

  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode, excludeRH: false });
  const [selectHumanResource, setSelectHumanResource] = useState<HumanResourcesItem[]>([]);
  const [drawerTransfer, setDrawerTransfer] = useState(false);
  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawertEditValues, setDrawerEditValues] = useState(false);
  const [drawerEditRatio, setDrawerEditRatio] = useState(false);
  const [drawerUnion, setDrawerUnion] = useState(false);

  const [originProject, setOriginProject] = useState<ProjectResponse | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');

  const { data: humanResources } = useSearchHumanResourcesQuery(
    {
      period: period.selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originProjectSearch || '',
    },
    {
      skip: !period.selectedPeriod?.date || !code || !selectedFinancialSubGroup,
    },
  );

  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      setSelectedFinancialSubGroup(financialsubGroup[0]);
    }
  }, [financialsubGroup, setSelectedFinancialSubGroup]);
  const [selectHumanResourceItem, setSelectedHumanResourceItem] = useState<HumanResourcesItem>();

  const handleDrowersUnion = () => {
    setDrawerUnion(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ minWidth: theme.spacing(31) }}>
              <Typography component="p" variant="fieldHeader">
                {t('common:group')}
              </Typography>
              <FormControl sx={{ width: theme.spacing(30) }}>
                <Select
                  displayEmpty
                  value={selectedFinancialSubGroup?.id || ({} as SubGroup)}
                  onChange={(e) => {
                    const selected = financialsubGroup?.find((item) => item.id === e.target.value);
                    if (selected) setSelectedFinancialSubGroup(selected);
                  }}
                  sx={{ height: 32, fontSize: 14 }}
                >
                  {financialsubGroup &&
                    financialsubGroup.map((item: SubGroup) => (
                      <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                        {`${item.name}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originProject}
              onChange={(value) => {
                setOriginProject(value);
              }}
              equalityTest={(option, value) => option?.code === value?.code}
              getOptionLabel={(option) =>
                `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
              }
              onSearch={(value) => {
                setOriginProjectSearch(value);
              }}
              options={[]}
              placeholder={t('common:type.to.search') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box>
            <Box sx={{ display: 'flex' }}>
              <Typography sx={{ width: theme.spacing(16) }}>{`${t('system.total')}:  `}</Typography>
              <Typography sx={{ marginLeft: 'auto' }}>
                {formatterMonetaryValue(humanResources?.header.totalValue || 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Typography sx={{ width: theme.spacing(16) }}>{`${t('erp.total')}:  `}</Typography>
              <Typography sx={{ marginLeft: 'auto' }}>
                {formatterMonetaryValue(humanResources?.header.totalERP || 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Typography sx={{ width: theme.spacing(16), fontWeight: 'bold' }}>{`${t('difference')}:  `}</Typography>
              <Typography sx={{ marginLeft: 'auto' }}>
                {formatterMonetaryValue(
                  ((humanResources?.header.totalValue || 0) - (humanResources?.header.totalERP || 0)).toFixed(2),
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto', mt: 'auto', mb: 0.5 }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleDrowersUnion()}
              disabled={selectHumanResource.length <= 1}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>
      <HumanResourcesTable
        selectHumanResource={selectHumanResource}
        setSelectHumanResource={setSelectHumanResource}
        setDrawerEdit={setDrawerEdit}
        setSelectedHumanResourceItem={setSelectedHumanResourceItem}
        selectHumanResourceItem={selectHumanResourceItem as HumanResourcesItem}
        setDrawerTransfer={setDrawerTransfer}
        setDrawerEditRatio={setDrawerEditRatio}
        humanResourceData={humanResources}
        setEditValues={setDrawerEditValues}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right">
        {selectedFinancialSubGroup?.name === 'Serviço PJ' ? (
          <NewEditHumanResourcesDetailsPJ
            selectItem={selectHumanResourceItem as HumanResourcesItem}
            setDrawerOpen={setDrawerEdit}
          />
        ) : (
          <NewEditHumanResourcesDetails
            selectItem={selectHumanResourceItem as HumanResourcesItem}
            setDrawerOpen={setDrawerEdit}
          />
        )}
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right">
        <NewHumanResourcesTransfer
          selectedItem={selectHumanResourceItem as HumanResourcesItem}
          setDrawerOpen={setDrawerTransfer}
          groupCode={groupCode}
        />
      </LateralDrawer>
      <LateralDrawer open={drawertEditValues} setOpen={setDrawerEditValues} position="right" width={theme.spacing(80)}>
        <NewEditValuesHumanResources
          selectedItem={selectHumanResourceItem as HumanResourcesItem}
          setDrawerOpen={setDrawerEditValues}
        />
      </LateralDrawer>
      <LateralDrawer open={drawerEditRatio} setOpen={setDrawerEditRatio} position="right">
        <NewEditRatioHumanResources
          selectItem={selectHumanResourceItem as HumanResourcesItem}
          setDrawerOpen={setDrawerEditRatio}
          project={code || ''}
        />
      </LateralDrawer>
      <LateralDrawer open={drawerUnion} setOpen={setDrawerUnion} position="right">
        <NewUnion
          selectItem={selectHumanResource}
          setSelectItem={setSelectHumanResource}
          setDrawerOpen={setDrawerUnion}
        />
      </LateralDrawer>
    </Box>
  );
}

export default HumanResources;
